import { ReactNode, CSSProperties } from "react";
import cobertura from "assets/images/icons/ocupacoes/rdo/cobertura.svg";
import feijao from "assets/images/icons/ocupacoes/rdo/feijao.svg";
import gergelim from "assets/images/icons/ocupacoes/rdo/gergelim.svg";
import milhoPipoca from "assets/images/icons/ocupacoes/rdo/milho_pipoca.svg";
import pastagem from "assets/images/icons/ocupacoes/rdo/pastagem.svg";
import preparo from "assets/images/icons/ocupacoes/rdo/preparo.svg";
import algodao from "assets/images/relatoriodiario/algodao.png";
import amendoim from "assets/images/relatoriodiario/amendoim.png";
import arroz from "assets/images/relatoriodiario/arroz.png";
import brachiaria from "assets/images/relatoriodiario/brachiaria.png";
import crotalaria from "assets/images/relatoriodiario/crotalaria.png";
import eucalipto from "assets/images/relatoriodiario/eucalipto.png";
import milheto from "assets/images/relatoriodiario/milheto.png";
import milho from "assets/images/relatoriodiario/milho.png";
import soja from "assets/images/relatoriodiario/sojarelatoriodiario.png";
import sorgo from "assets/images/relatoriodiario/sorgo.png";
import trigo from "assets/images/relatoriodiario/trigo.png";

type OcupacaoTheme = {
  background: string;
  border: string;
  filter?: string;
};

export type RdoOcupacaoIconVisual = OcupacaoTheme & {
  icon: ReactNode;
};

const THEME_FALLBACK: OcupacaoTheme = {
  background: "#F4F6F8",
  border: "#D5DCE3",
};

const ICON_SIZE = 36;

function normalizeOcupacao(value?: string | null): string {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function resolveTheme(key: string): OcupacaoTheme {
  if (key.includes("estilosante")) {
    return {
      background: "#E8F8F1",
      border: "#9AD9BE",
      filter: "hue-rotate(100deg) saturate(1.15)",
    };
  }
  if (key.includes("crotalaria")) {
    return { background: "#F2FBE6", border: "#B7E07A" };
  }
  if (key.includes("carinata")) {
    return {
      background: "#FFF8E0",
      border: "#F0D56A",
      filter: "hue-rotate(48deg) saturate(1.25)",
    };
  }
  if (key.includes("calcario")) {
    return {
      background: "#F1F2F4",
      border: "#C8CED6",
      filter: "grayscale(0.45) sepia(0.2) brightness(1.05)",
    };
  }
  if (key.includes("preparo")) {
    return { background: "#EEF5FF", border: "#AFC8EF" };
  }
  if (key.includes("milho pipoca") || key.includes("pipoca")) {
    return { background: "#FFF3E8", border: "#F5C49A" };
  }
  if (key.includes("feijao")) return { background: "#FBF0DC", border: "#E8C98A" };
  if (key.includes("gergelim")) return { background: "#FFF6E9", border: "#E8C07A" };
  if (key.includes("milheto")) return { background: "#FFF9E6", border: "#E8D48A" };
  if (key.includes("sorgo")) return { background: "#FDEEE8", border: "#E8B09A" };
  if (key.includes("brachiaria")) return { background: "#EAF8EF", border: "#A3D9B5" };
  if (key.includes("pastagem")) return { background: "#EAF8EF", border: "#A3D9B5" };
  if (key.includes("soja")) return { background: "#EAF7F0", border: "#9AD4B0" };
  if (key.includes("milho")) return { background: "#FFFBEA", border: "#E8D56A" };
  if (key.includes("algodao")) return { background: "#F3F0FA", border: "#C9BDE8" };
  if (key.includes("arroz")) return { background: "#F4F8F0", border: "#B8D4A8" };
  if (key.includes("amendoim")) return { background: "#FFF6E9", border: "#E8C07A" };
  if (key.includes("eucalipto")) return { background: "#EAF7F0", border: "#9AD4B0" };
  if (key.includes("trigo")) return { background: "#FFFBEA", border: "#E8D56A" };
  return THEME_FALLBACK;
}

/** PNG de relatoriodiario quando houver equivalente; senão SVG legado. */
function resolveOcupacaoSrc(key: string): string {
  if (key.includes("milho pipoca") || key.includes("pipoca")) return milhoPipoca;
  if (key.includes("crotalaria")) return crotalaria;
  if (key.includes("estilosante") || key.includes("carinata")) return cobertura;
  if (key.includes("feijao")) return feijao;
  if (key.includes("gergelim")) return gergelim;
  if (key.includes("milheto")) return milheto;
  if (key.includes("sorgo")) return sorgo;
  if (key.includes("brachiaria")) return brachiaria;
  if (key.includes("pastagem")) return pastagem;
  if (key.includes("algodao")) return algodao;
  if (key.includes("soja")) return soja;
  if (key.includes("milho")) return milho;
  if (key.includes("arroz")) return arroz;
  if (key.includes("amendoim")) return amendoim;
  if (key.includes("eucalipto")) return eucalipto;
  if (key.includes("trigo")) return trigo;
  return preparo;
}

export function getRdoOcupacaoIconVisual(
  ocupacao?: string | null,
  size = ICON_SIZE
): RdoOcupacaoIconVisual {
  const key = normalizeOcupacao(ocupacao);
  const theme = resolveTheme(key);
  const src = resolveOcupacaoSrc(key);

  const imgStyle: CSSProperties = {
    display: "block",
    objectFit: "contain",
    ...(theme.filter ? { filter: theme.filter } : {}),
  };

  return {
    ...theme,
    icon: (
      <img
        src={src}
        width={size}
        height={size}
        alt=""
        aria-hidden
        style={imgStyle}
      />
    ),
  };
}

export function getRdoOcupacaoIcon(
  ocupacao?: string | null,
  size = ICON_SIZE
): ReactNode {
  return getRdoOcupacaoIconVisual(ocupacao, size).icon;
}
